import { ContentStatus } from "__generated__/types";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { ListContainer } from "common/components/lists/ListContainer";
import { useUpdateContent } from "domain/common/content-mutations";
import update from "immutability-helper";
import React, { useCallback, useEffect, useState } from "react";
import { SimulationListElement } from "./SimulationListElement";

export type ListElementCategory = "SimulationCaseElement";

export type ListElementType = "SimulationCase";

export type SimulationCaseListElementBase = {
  id: string;
  category: ListElementCategory;
  type: ListElementType;
  status: ContentStatus;
  title: string;
};

export type ListElementSimulationCase = SimulationCaseListElementBase & {
  category: "SimulationCaseElement";
};

export type ListElement = ListElementSimulationCase;

type Props = {
  showElementIcons?: boolean;
  elements: ListElement[];
  complete: () => void;
  //canDelete: boolean;
  onSaveListOrder?(id: string, moveToAfterElement: ListElement | null): void;
};

export function SimulationList({
  showElementIcons = false,
  elements,
  complete,
  //canDelete = true,
  onSaveListOrder,
}: Props): React.ReactElement {
  const [draggableElements, setDraggableElements] = useState(elements);

  useEffect(() => {
    setDraggableElements(elements);
  }, [elements]);

  const [mutateDelete, { error, loading }] = useUpdateContent();

  const handleDelete = async (id: string) => {
    const result = await mutateDelete({
      variables: {
        input: {
          id,
          status: { updatedValue: ContentStatus.DELETED },
        },
      },
    });
    if (result.data) {
      complete();
    }
  };

  const moveElement = useCallback((dragIndex: number, hoverIndex: number) => {
    setDraggableElements((prevElements) =>
      update(prevElements, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevElements[dragIndex]],
        ],
      }),
    );
  }, []);

  const saveOrder = useCallback(
    (dragIndex: number, dropIndex: number, id: string) => {
      if (onSaveListOrder) {
        const moveToAfterElement =
          dropIndex === 0 ? null : (draggableElements[dropIndex - 1] ?? null);
        onSaveListOrder(id, moveToAfterElement);
      }
    },
    [draggableElements],
  );

  return (
    <ListContainer>
      {draggableElements.map((element, index) => {
        return (
          <SimulationListElement
            key={element.id}
            index={index}
            element={element}
            showIcon={showElementIcons}
            isLoading={loading}
            moveElement={moveElement}
            saveOrder={saveOrder}
            // TODO: Improve logic for when elements are draggable and not
            canDrag={!!onSaveListOrder}
          />
        );
      })}
      <MutationLoadingOrError loading={loading} error={error} />
    </ListContainer>
  );
}
